/**
 * RecordDetailPanel
 *
 * Right pane: the full record editor. Reuses the existing Entity component,
 * which reads the selected entity/record from the nearest `PageScope` (opened
 * by EntitiesPage from the URL). Shows a clean placeholder until a record is
 * open, and suppresses Entity's own "New Record" action — creation lives in the
 * list pane, so the editor only carries record-scoped controls.
 *
 * It owns `formUndoStack` for this surface, through `useFormUndoKeybindings`: the
 * record's tables record deletes and cell edits into that stack, and this pane swaps
 * records without unmounting, so the record path — not the mount — is what clears
 * them. Without it, a delete on one record stays on the buttons while a second record
 * is open (SYD-2447).
 *
 * It carries the undo/redo pair beside that ownership, because this pane reaches an
 * ArTable through `Entity` and never through `FormControl`: without the strip, a row
 * delete here — which no longer asks for confirmation — would be reversible only by
 * the chord. The strip stays over both placeholders, where the empty stack disables
 * it, rather than appearing with the record.
 *
 * `DesktopKeepControl` shares that strip for the same reason: it is disabled over
 * both placeholders rather than absent, so the toolbar does not reflow when a
 * record opens. It stays enabled on a read-only record — the pin is on the
 * reader's own user document, not on the record. That "never absent" holds only
 * for a reader who has the `fileSync` right; without it the control is not on
 * the strip at all, because Syncretic File Sync is unreleased and a disabled
 * button would advertise a capability nobody can obtain. Gating here rather
 * than inside the control is what keeps its pin listener unopened.
 *
 * When RecordsBrowser stacks below md it passes `onBack`, and the strip opens
 * with a back control to the list route; the keep and undo controls stay
 * grouped at the far end. The back control is default size, 32px, because it
 * only renders on phones.
 */
import { FC, useRef } from 'react';
import { Button, Empty, theme } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import { useFormUndoKeybindings } from '@/use/form/useFormUndoKeybindings';
import FlexBox from '../atoms/FlexBox';
import FlexCol from '../atoms/FlexCol';
import FormUndoButtons from '../artifacts/form/FormUndoButtons';
import { useFileSyncAccess } from '@/use/desktopSync/useFileSyncAccess';
import { DesktopKeepControl } from '../desktopSync';
import Entity from '../Entity';

interface RecordDetailPanelProps {
  entityId: string | undefined;
  recordId: string | undefined;
  /** Set when RecordsBrowser stacks below md: the back control returns to the list route. */
  onBack?: () => void;
}

const centered = (description: string) => (
  <div
    style={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
    }}
  >
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} />
  </div>
);

const RecordDetailPanel: FC<RecordDetailPanelProps> = ({
  entityId,
  recordId,
  onBack,
}) => {
  const { token } = theme.useToken();
  const contentRef = useRef<HTMLDivElement>(null);
  const hasFileSyncAccess = useFileSyncAccess();

  useFormUndoKeybindings(
    contentRef,
    entityId && recordId
      ? `entities/${entityId}/records/${recordId}`
      : undefined,
  );

  return (
    <FlexCol
      gap={0}
      style={{
        flex: 1,
        minWidth: 0,
        height: '100%',
        minHeight: 0,
        backgroundColor: token.colorBgLayout,
      }}
    >
      <FlexBox
        noGrow
        justifyEnd={!onBack}
        gap={5}
        style={{
          width: '100%',
          paddingInline: token.padding,
          paddingTop: token.paddingXS,
          boxSizing: 'border-box',
        }}
      >
        {onBack && (
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            aria-label="Back to record list"
          >
            Records
          </Button>
        )}
        <FlexBox noGrow gap={5}>
          {hasFileSyncAccess && (
            <DesktopKeepControl
              artifactPath={
                entityId && recordId
                  ? `entities/${entityId}/records/${recordId}`
                  : undefined
              }
              artifactLabel="record"
            />
          )}
          <FormUndoButtons surface="record controls" />
        </FlexBox>
      </FlexBox>
      {entityId && recordId ? (
        <div
          ref={contentRef}
          style={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            overflowY: 'auto',
            padding: token.padding,
            boxSizing: 'border-box',
          }}
        >
          <Entity showCreate={false} />
        </div>
      ) : (
        centered(
          entityId
            ? 'Select a record to view, or create a new one'
            : 'Select a record type to begin',
        )
      )}
    </FlexCol>
  );
};

export default RecordDetailPanel;
